import { initializeApp, type FirebaseApp } from "firebase/app";
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from "firebase/app-check";
import { getFunctions, connectFunctionsEmulator, type Functions } from "firebase/functions";
import { FUNCTIONS_REGION } from "@ks-gen/shared";
import { RECAPTCHA_SITE_KEY, USE_EMULATORS } from "./env";

/**
 * Lazy Firebase client init. The web config is injected at build by App Hosting
 * (FIREBASE_WEBAPP_CONFIG -> __FIREBASE_CONFIG__ in vite.config.ts). App Check
 * (invisible reCAPTCHA Enterprise) attaches a token to every callable request;
 * the functions enforce it (enforceAppCheck), so calls fail until the real
 * VITE_RECAPTCHA_SITE_KEY is set and App Check is registered.
 *
 * Region comes from @ks-gen/shared (single source of truth), so it always
 * matches the functions' deploy region.
 */
declare const __FIREBASE_CONFIG__: Record<string, string> | undefined;

let appInstance: FirebaseApp | null = null;
let functionsClient: Functions | null = null;

/** The injected web config, or undefined when absent (local dev / mock mode). */
export function getFirebaseConfig(): Record<string, string> | undefined {
  return typeof __FIREBASE_CONFIG__ !== "undefined" ? __FIREBASE_CONFIG__ : undefined;
}

/**
 * Memoized FirebaseApp. Deliberately does NOT initialize App Check — that stays
 * with getFirebaseFunctions so a page that only needs Analytics (which never
 * calls a callable) doesn't pull in reCAPTCHA on load.
 */
export function getFirebaseApp(): FirebaseApp {
  if (appInstance) return appInstance;
  const config = getFirebaseConfig();
  if (!config) {
    throw new Error("Firebase web config missing (FIREBASE_WEBAPP_CONFIG not present at build).");
  }
  appInstance = initializeApp(config);
  return appInstance;
}

export function getFirebaseFunctions(): Functions {
  if (functionsClient) return functionsClient;

  const app = getFirebaseApp();

  if (import.meta.env.DEV) {
    // App Check debug token for local/emulator use.
    (self as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN?: boolean }).FIREBASE_APPCHECK_DEBUG_TOKEN = true;
  }
  if (RECAPTCHA_SITE_KEY) {
    initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(RECAPTCHA_SITE_KEY),
      isTokenAutoRefreshEnabled: true,
    });
  }

  functionsClient = getFunctions(app, FUNCTIONS_REGION);
  if (USE_EMULATORS) connectFunctionsEmulator(functionsClient, "127.0.0.1", 5001);
  return functionsClient;
}
